import React, { useState, useEffect } from 'react';
import { Spin, Button } from 'antd';
import {
    CheckCircleFilled,
    CloseCircleFilled,
    ExclamationCircleFilled,
    ClockCircleFilled,
    UserOutlined,
    IdcardOutlined,
    DollarOutlined,
    RightOutlined,
    FormOutlined,
} from '@ant-design/icons';
import { useNavigate } from 'react-router-dom';
import axiosInstance from '../../utils/axiosConfig';
import UserLayout from '../Layout/UserLayout';

/* ─── Màu sắc tham khảo từ email ─────────────────────────────── */
const NAVY   = '#1a237e';
const BLUE_BG = '#d3e3fd';
const WHITE  = '#fff';

/* ─── Config từng mục checklist ─────────────────────────────── */
const ITEM_CONFIG = {
    personal_info: { icon: <UserOutlined />,       path: '/student-profile', actionLabel: 'Cập nhật thông tin' },
    evidence:      { icon: <IdcardOutlined />,      path: '/student-profile', actionLabel: 'Upload giấy tờ' },
    admissions:    { icon: <FormOutlined />,        path: '/student-profile', actionLabel: 'Đăng ký nguyện vọng' },
    payment:       { icon: <DollarOutlined />,      path: '/payment',         actionLabel: 'Nộp lệ phí' },
};

/* ─── Config trạng thái nổi bật ──────────────────────────────── */
const STATUS_CONFIG = {
    draft: {
        icon:       <ExclamationCircleFilled />,
        iconColor:  '#fa8c16',
        bg:         'linear-gradient(135deg, #fff7e6 0%, #ffe7ba 100%)',
        border:     '#ffa940',
        titleColor: '#ad4e00',
        title:      'Hồ sơ chưa hoàn thiện',
        desc:       'Vui lòng hoàn thành các bước còn thiếu bên dưới',
        badgeBg:    '#fa8c16',
        badgeLabel: 'CHƯA HOÀN THIỆN',
    },
    submitted: {
        icon:       <ClockCircleFilled />,
        iconColor:  '#1677ff',
        bg:         'linear-gradient(135deg, #e6f4ff 0%, #bae0ff 100%)',
        border:     '#4096ff',
        titleColor: '#0958d9',
        title:      'Đã nộp hồ sơ – Chờ thanh toán',
        desc:       'Hồ sơ đang chờ xác nhận lệ phí xét tuyển',
        badgeBg:    '#1677ff',
        badgeLabel: 'CHỜ THANH TOÁN',
    },
    completed: {
        icon:       <CheckCircleFilled />,
        iconColor:  '#52c41a',
        bg:         'linear-gradient(135deg, #f6ffed 0%, #d9f7be 100%)',
        border:     '#95de64',
        titleColor: '#237804',
        title:      'Hồ sơ hoàn thành',
        desc:       'Hồ sơ của bạn đã đầy đủ',
        badgeBg:    '#52c41a',
        badgeLabel: 'HOÀN THÀNH',
    },
};

const ProfileChecklist = () => {
    const [loading, setLoading]     = useState(true);
    const [checklist, setChecklist] = useState([]);
    const [status, setStatus]       = useState('draft');
    const navigate = useNavigate();

    useEffect(() => {
        axiosInstance.get('/api/v1/student-profile')
            .then(res => {
                if (res.data?.success) {
                    const data = res.data.data;
                    setChecklist(data.checklist || []);
                    setStatus(data.status || 'draft');
                }
            })
            .finally(() => setLoading(false));
    }, []);

    const statusCfg  = STATUS_CONFIG[status] || STATUS_CONFIG.draft;
    const required   = checklist.filter(i => i.required);
    const doneCount  = required.filter(i => i.completed).length;
    const totalCount = required.length;

    return (
        <UserLayout title="Checklist hồ sơ">
            {loading ? (
                <div style={{ textAlign: 'center', padding: 60 }}>
                    <Spin size="large" />
                </div>
            ) : (
                <div style={{ maxWidth: 720, margin: '0 auto' }}>

                    {/* ── Header tham khảo email ── */}
                    <div style={{
                        background: NAVY, color: WHITE,
                        borderRadius: '8px 8px 0 0',
                        padding: '20px 28px',
                        textAlign: 'center',
                    }}>
                        <div style={{ fontSize: 18, fontWeight: 700, marginBottom: 4 }}>
                            Checklist hồ sơ đăng ký dự tuyển
                        </div>
                        <div style={{ fontSize: 16, marginBottom: 4 }}>
                            (Học tại Hà Nội, Mã trường: <strong>GTA</strong>) &nbsp;|&nbsp; (Học tại Phú Thọ, Mã trường: <strong>GTP</strong>)
                        </div>
                    </div>

                    {/* ── Ô trạng thái nổi bật ── */}
                    <div style={{
                        background: statusCfg.bg,
                        border: `2px solid ${statusCfg.border}`,
                        borderTop: 'none',
                        borderRadius: '0 0 0 0',
                        padding: '20px 28px',
                        display: 'flex',
                        alignItems: 'center',
                        gap: 18,
                    }}>
                        {/* Icon lớn */}
                        <div style={{
                            fontSize: 48,
                            color: statusCfg.iconColor,
                            lineHeight: 1,
                            flexShrink: 0,
                        }}>
                            {statusCfg.icon}
                        </div>

                        {/* Nội dung */}
                        <div style={{ flex: 1 }}>
                            <div style={{
                                display: 'inline-block',
                                background: statusCfg.badgeBg,
                                color: WHITE,
                                fontSize: 13,
                                fontWeight: 700,
                                letterSpacing: '0.08em',
                                borderRadius: 4,
                                padding: '2px 8px',
                                marginBottom: 6,
                            }}>
                                {statusCfg.badgeLabel}
                            </div>
                            <div style={{
                                fontSize: 18,
                                fontWeight: 700,
                                color: statusCfg.titleColor,
                                lineHeight: 1.3,
                            }}>
                                {statusCfg.title}
                            </div>
                            <div style={{ fontSize: 13, color: '#666', marginTop: 4 }}>
                                {statusCfg.desc}
                            </div>
                        </div>

                        {/* Tiến độ */}
                        <div style={{
                            textAlign: 'center',
                            flexShrink: 0,
                            background: 'rgba(255,255,255,0.7)',
                            borderRadius: 10,
                            padding: '10px 18px',
                            border: `1px solid ${statusCfg.border}`,
                        }}>
                            <div style={{
                                fontSize: 28,
                                fontWeight: 800,
                                color: statusCfg.iconColor,
                                lineHeight: 1,
                            }}>
                                {doneCount}/{totalCount}
                            </div>
                            <div style={{ fontSize: 11, color: '#888', marginTop: 4 }}>
                                bước hoàn thành
                            </div>
                        </div>
                    </div>

                    {/* ── Progress bar ── */}
                    <div style={{ background: '#e9ecef', height: 8 }}>
                        <div style={{
                            height: 8,
                            width: `${totalCount > 0 ? (doneCount / totalCount) * 100 : 0}%`,
                            background: statusCfg.badgeBg,
                            transition: 'width 0.5s ease',
                            borderRadius: '0 4px 4px 0',
                        }} />
                    </div>

                    {/* ── Danh sách checklist ── */}
                    <div style={{
                        background: BLUE_BG,
                        border: '1px solid #b6d0f7',
                        borderTop: 'none',
                        padding: '20px 24px',
                        display: 'flex',
                        flexDirection: 'column',
                        gap: 12,
                        borderRadius: '0 0 8px 8px',
                    }}>
                        {checklist.map((item) => {
                            const cfg = ITEM_CONFIG[item.key] || {};

                            return (
                                <div
                                    key={item.key}
                                    style={{
                                        background: WHITE,
                                        borderRadius: 8,
                                        padding: '14px 18px',
                                        display: 'flex',
                                        alignItems: 'center',
                                        gap: 14,
                                        border: item.completed
                                            ? '1px solid #b7eb8f'
                                            : '1px solid #ffd591',
                                    }}
                                >
                                    {/* Status icon */}
                                    <div style={{ fontSize: 22, flexShrink: 0 }}>
                                        {item.completed
                                            ? <CheckCircleFilled style={{ color: '#52c41a' }} />
                                            : <CloseCircleFilled style={{ color: '#ff7875' }} />
                                        }
                                    </div>

                                    {/* Icon + Label */}
                                    <div style={{ color: NAVY, fontSize: 18, flexShrink: 0 }}>
                                        {cfg.icon}
                                    </div>
                                    <div style={{ flex: 1 }}>
                                        <div style={{
                                            fontWeight: 600,
                                            fontSize: 14,
                                            color: '#333',
                                        }}>
                                            {item.label}
                                        </div>
                                        {!item.completed && (
                                            <div style={{ fontSize: 12, color: '#fa8c16', marginTop: 2 }}>
                                                Chưa hoàn thành
                                            </div>
                                        )}
                                    </div>

                                    {/* Action button nếu chưa xong */}
                                    {!item.completed && cfg.path && (
                                        <Button
                                            size="small"
                                            type="primary"
                                            ghost
                                            icon={<RightOutlined />}
                                            onClick={() => navigate(cfg.path)}
                                            style={{ flexShrink: 0 }}
                                        >
                                            {cfg.actionLabel}
                                        </Button>
                                    )}
                                </div>
                            );
                        })}
                    </div>

                    {/* ── Lưu ý ── */}
                    <div style={{
                        background: '#fff3cd',
                        borderLeft: '4px solid #ffc107',
                        padding: '14px 20px',
                        marginTop: 20,
                        borderRadius: 4,
                        fontSize: 13,
                        lineHeight: 1.7,
                    }}>
                        <strong>Lưu ý quan trọng:</strong> Thí sinh phải <strong>ĐĂNG KÝ NGUYỆN VỌNG CHÍNH THỨC</strong> trên hệ thống của Bộ Giáo dục và Đào tạo từ ngày <strong>02/7/2026</strong> đến <strong>17h00 ngày 14/7/2026</strong> theo quy định.
                    </div>

                    {/* ── Thông tin liên hệ ── */}
                    <div style={{
                        background: '#e3f2fd',
                        borderRadius: 6,
                        padding: '14px 20px',
                        marginTop: 16,
                        fontSize: 13,
                        lineHeight: 1.8,
                        color: '#333',
                    }}>
                        <div style={{ fontWeight: 600, marginBottom: 4, color: NAVY }}>
                            Văn phòng tuyển sinh – Tại Hà Nội:
                        </div>
                        <div>Địa chỉ: 54 Triều Khúc – Thanh Liệt – Hà Nội</div>
                        <div>Điện thoại: <strong>024.3552.6713</strong> &nbsp;|&nbsp; Email: <strong>xettuyen@utt.edu.vn</strong></div>

                        <div style={{ fontWeight: 600, marginBottom: 4, color: NAVY }}>
                            Văn phòng tuyển sinh – Tại Phú Thọ:
                        </div>
                        <div>Địa chỉ: Số 278 Lam Sơn, phường Vĩnh Yên, Phú Thọ</div>
                        <div>Điện thoại: <strong>0211.386.7404</strong></div>
                    </div>

                </div>
            )}
        </UserLayout>
    );
};

export default ProfileChecklist;
